import CustomerBenefit from '../../shared_components/CustomerBenefit/CustomerBenefit';
import WizardPagetemplate from '../../templates/WizardPagetemplate';
import { Box, Button, PageHeader, Link, Message, Grid, GridItem } from '@flos/react-ui';
import ShoppingBasketCoverableList from './components/ShoppingBasketCoverableList';
import TotalPrice from '../../shared_components/TotalPrice/TotalPrice';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { periodicityUnit, paymentFrequencyMap } from '../../utils/currencyUtil';
import { useHippoText } from '../../hooks/useHippoText';
import { sortedCoverablesListSelector } from '../../recoil/selectors/sortedCoverablesListSelector';
import { priceTaxAtom } from '../../recoil/atoms/priceTaxAtom';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import { businessFlowSelector } from '../../recoil/selectors/getBusinessFlowSelector';
import { isLoggedOnSelector } from '../../recoil/selectors/isLoggedOnSelector';
import LoadingModal from '../../shared_components/LoadingModal/LoadingModal';
import { useRef, useState } from 'react';
import { useQuoteJob } from '../../hooks/useQuoteJob';
import { useAwaitQuoteJob } from '../../hooks/useAwaitQuotedJob';
import { issuedCoverablesAndPricesAtom } from '../../recoil/atoms/issuedCoverablesAndPricesAtom';
import { onsaInfoAtom } from '../../recoil/atoms/onsaInfoAtom';
import { DRAFT, QUOTED } from '../../recoil/types/types';
import GiftCertificateShoppingBasket from '../../shared_components/GiftCertificate/GiftCertificateShoppingBasket';
import CustomerStopModal from '../../shared_components/CustomerStopModal/CustomerStopModal';
import type { CustomerStopCause } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { accountHolderAtom } from '../../recoil/atoms/accountHolderAtom';
import { refreshMissingInformationAtom } from '../../recoil/atoms/refreshMissingInformationAtom';
import { onsaProductStatusListAtom } from '../../recoil/atoms/onsaProductStatusListAtom';
import EmptyShoppingBasketPage from './EmptyShoppingBasketPage';
import { missingInformationAtom } from '../../recoil/atoms/missingInformationAtom';
import { onsaProgressSelector } from '../../recoil/selectors/onsaProgressSelector';
import { externalCasesAtom } from '../../recoil/atoms/externalCasesAtom';
import { doesCoverablesContainChangedStatus } from '../../utils/coverablesUtil';

const ShoppingBasketPage: WizardPageComponent = () => {
    const { wizardNextStep, wizardPreviousStep, wizardGotoStep } = useWizardNavigation();
    const [jobData, setJobData] = useRecoilState(jobAtom);
    const priceTaxData = useRecoilValue(priceTaxAtom);
    const selectedPaymentFrequency =
        jobData.selectedPaymentFrequency ?? paymentFrequencyMap.monthly;
    const paymentFrequencyPostfix = periodicityUnit[selectedPaymentFrequency];
    const { sortedCoverables, sortedRemovedCoverables } = useRecoilValue(
        sortedCoverablesListSelector
    );
    const CHOCoverable = sortedCoverables.filter(
        (cov) => cov.flavourName === 'Ejerskifteforsikring'
    );
    const isShoppingBasketEmpty = sortedCoverables.concat(sortedRemovedCoverables).length === 0;
    const getHippoText = useHippoText();
    const businessFlow = useRecoilValue(businessFlowSelector);
    const isLoggedOn = useRecoilValue(isLoggedOnSelector);
    const { quoteJob } = useQuoteJob();
    const { awaitQuoteJob } = useAwaitQuoteJob();
    const redirectedFromNemLogin = useRecoilValue(onsaInfoAtom).onsaStep === 'ShoppingBasketPage';
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [stopCause, setStopCause] = useState<CustomerStopCause>('None');
    const hasTriggeredQuoting = useRef(false);
    const automaticQuoteAndRedirect =
        redirectedFromNemLogin &&
        jobData.jobStatus === DRAFT &&
        !showLoadingModal &&
        !hasTriggeredQuoting.current;
    const refreshCoverableAndPricesSelectors = useSetRecoilState(issuedCoverablesAndPricesAtom);
    const resetOnsaProgress = useResetRecoilState(onsaProgressSelector);
    const resetProductStatuses = useResetRecoilState(onsaProductStatusListAtom);
    const resetExternalCases = useResetRecoilState(externalCasesAtom);
    const hasCoverableWithChangedStatus = doesCoverablesContainChangedStatus(sortedCoverables);
    const accountHolder = useRecoilValue(accountHolderAtom);
    const refreshMissingInformation = useSetRecoilState(refreshMissingInformationAtom);
    const resetMissingInformation = useResetRecoilState(missingInformationAtom);

    const quoteJobInBackground = async (): Promise<void> => {
        const response = await quoteJob();
        hasTriggeredQuoting.current = true;
        if (response?.stopCause !== 'None') {
            setStopCause(response?.stopCause);
            setShowLoadingModal(false);
        } else {
            await awaitQuoteJob();
        }
    };

    const onFinished = (): void => {
        if (stopCause === 'None') {
            setJobData((jobDTO) => ({ ...jobDTO, jobStatus: QUOTED }));
            refreshCoverableAndPricesSelectors({});
            refreshMissingInformation({});
            resetMissingInformation();
            wizardGotoStep('ShoppingBasketSummaryPage');
        }
    };

    const goNextStep = (): void => {
        if (isLoggedOn) {
            if (jobData.jobStatus === DRAFT) {
                setShowLoadingModal(true);
            } else {
                refreshMissingInformation({});
                resetMissingInformation();
                wizardGotoStep('ShoppingBasketSummaryPage');
            }
        } else if (!redirectedFromNemLogin) {
            wizardGotoStep('MitIdLoginPage');
        }
    };

    if (automaticQuoteAndRedirect) {
        goNextStep();
    }

    if (isShoppingBasketEmpty) {
        return <EmptyShoppingBasketPage />;
    }

    return (
        <WizardPagetemplate
            hidePrevious
            hideNext
            onNext={wizardNextStep}
            onPrevious={wizardPreviousStep}
            continueButtonText="Accepter tilbuddet"
            showGiftCertificateBanner={false}
        >
            <Grid>
                <GridItem span={8} colStart={3}>
                    <PageHeader
                        title={getHippoText(
                            accountHolder.isCVR
                                ? 'DSS-SHOPPING-BASKET-CVR-TITLE'
                                : 'DSS-SHOPPING-BASKET-TITLE'
                        )}
                        align="center"
                    />
                    <div className="u-spacing-inset-l--squish-max u-spacing-inset-bottom-12 u-spacing-inset-bottom-none@xs">
                        {CHOCoverable.length > 0 && (
                            <Message theme="warning" className="u-spacing-stack-bottom-7">
                                {getHippoText('DSS-SHOPPING-BASKET-CHOLOB-WARNING')}
                            </Message>
                        )}
                        <ShoppingBasketCoverableList
                            coverables={sortedCoverables ?? []}
                            paymentFrequencyPostfix={paymentFrequencyPostfix}
                            paymentFrequency={selectedPaymentFrequency}
                        />
                        {sortedRemovedCoverables.length > 0 && (
                            <ShoppingBasketCoverableList
                                coverables={sortedRemovedCoverables ?? []}
                                paymentFrequency={selectedPaymentFrequency}
                                paymentFrequencyPostfix={paymentFrequencyPostfix}
                                shouldDisplayCoverablesAsCancelled
                            />
                        )}
                        {businessFlow === 'ONSA' && (
                            <Link
                                onClick={() => {
                                    resetOnsaProgress();
                                    resetProductStatuses();
                                    resetExternalCases();
                                    wizardGotoStep('ProductSelectorPage');
                                }}
                            >
                                <div
                                    className={
                                        'u-flex-box u-flex-box--justify-center u-bg-primary-2-light u-border-primary-2-medium u-border-dashed' +
                                        ' u-spacing-inset-5 u-border-radius-m u-spacing-inset-7@md u-spacing-stack-top-7'
                                    }
                                >
                                    <span className="u-flex-box u-flex-box--center u-gap-3">
                                        <Button iconShape="plus" size="small" />
                                        <p className="u-spacing-stack-bottom-none paragraph--small">
                                            {getHippoText(
                                                'DSS-SHOPPING-BASKET-ADD-COVERABLE-BUTTON'
                                            )}
                                        </p>
                                    </span>
                                </div>
                            </Link>
                        )}
                        <Box
                            // eslint-disable-next-line max-len
                            className="u-spacing-inset-5 u-spacing-inset-7@md u-shadow-none u-spacing-stack-top-7 u-flex-box u-flex-box--col u-gap-5 u-gap-6@md"
                            theme="transparent"
                        >
                            <GiftCertificateShoppingBasket />

                            <CustomerBenefit shouldRenderShoppingBasketDesign />
                            {businessFlow !== 'OPC' && !hasCoverableWithChangedStatus && (
                                <TotalPrice
                                    totalPrice={
                                        selectedPaymentFrequency === paymentFrequencyMap.monthly
                                            ? priceTaxData.monthlyPrice?.amount ?? 0
                                            : priceTaxData.yearlyPrice?.amount ?? 0
                                    }
                                    showYearlyPriceField={
                                        selectedPaymentFrequency === paymentFrequencyMap.monthly
                                    }
                                    paymentFrequencyPostfix={paymentFrequencyPostfix}
                                    displayTaxesSubtitle
                                    shouldRenderShoppingBasketDesign
                                    isCVR={accountHolder.isCVR}
                                />
                            )}
                            <div className="u-display-flex@xl u-flex-justify-between@xl">
                                <div className="u-spacing-inset-bottom-4 u-spacing-inset-bottom-none@xl">
                                    <Button
                                        onClick={() => {
                                            goNextStep();
                                        }}
                                    >
                                        {getHippoText('DSS-COMMON-CONTINUE')}
                                    </Button>
                                </div>
                            </div>
                        </Box>
                    </div>
                    {showLoadingModal && (
                        <LoadingModal
                            loadingTitle={getHippoText('DSS-LOADING-HEADER')}
                            loadingText={getHippoText('DSS-LOADING-TEXT')}
                            showModal={showLoadingModal}
                            closeModal={() => {
                                setShowLoadingModal(false);
                            }}
                            backgroundTask={quoteJobInBackground}
                            onFinished={onFinished}
                        />
                    )}
                    {stopCause !== 'None' && (
                        <CustomerStopModal
                            showModal={true}
                            stopCause={stopCause}
                            closeModal={() => {
                                setStopCause('None');
                            }}
                        />
                    )}
                </GridItem>
            </Grid>
        </WizardPagetemplate>
    );
};

export default ShoppingBasketPage;
